import { useContext } from 'react';
import { css } from '@emotion/react';
import {
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined';
import Snackbar from '@/components/Snackbar';
import { GreyBody, OnwardPagination, PrimaryButton } from '@/styles/blocks';
import { colors } from '@/styles';
import { Context, ContextProvider } from './store';
import { PAGE_SIZE } from './hooks';
import { formatDimensions, formatInventoryRange, formatServiceTime, formatWeight } from './utils';
import DocumentLink from './DocumentLink';
import SKUModal from './SKUModal';
import DeleteSKUModal from './DeleteSKUModal';
import SKUImportModal from './SKUImportModal';

const BASE_COLUMNS = [
    { id: 'sku', label: 'SKU', sortable: true },
    { id: 'description', label: 'Description', sortable: true },
    { id: 'assembly_instructions', label: 'Assembly Instructions', sortable: false },
    { id: 'service_time', label: 'Service Time', sortable: true },
    { id: 'weight', label: 'Weight', sortable: true },
    { id: 'dimensions', label: 'Dimensions', sortable: true },
    { id: 'inventory', label: 'Inventory (min/max)', sortable: true },
];

const AssemblyInstructions = ({ documents }) => {
    const docs = documents || [];
    if (!docs.length) return '--';
    return docs.map((doc, i) => (
        <span key={doc.item_dimension_document_id}>
            {i > 0 ? ', ' : ''}
            <DocumentLink href={doc.link}>{doc.name}</DocumentLink>
        </span>
    ));
};

const SKUs = () => {
    const { state, loading, callbacks } = useContext(Context);
    const pageCount = Math.max(1, Math.ceil(state.totalCount / PAGE_SIZE));
    const { sortBy, isCarrierAdmin, orgClients, selectedEntity, currentClientId, readOnly } = state;

    // Carrier admins get an "Owned by" column (first) + entity filter, and can only manage their own rows.
    const columns = [
        ...(isCarrierAdmin ? [{ id: 'owned_by', label: 'Owned by', sortable: false }] : []),
        ...BASE_COLUMNS,
        ...(readOnly ? [] : [{ id: 'actions', label: '', sortable: false, align: 'right' }]),
    ];
    const canManage = (sku) => !readOnly && (!isCarrierAdmin || sku.client_id === currentClientId);

    const onSort = (id) => () => {
        const isActive = sortBy?.id === id;
        callbacks.setSortBy({ id, desc: isActive ? !sortBy.desc : false });
    };

    const emptyText = loading.init
        ? 'Loading SKUs…'
        : readOnly
        ? 'No SKUs yet.'
        : 'No SKUs yet — add one or import a CSV to get started.';

    return (
        <div
            css={css`
                height: 60vh;
                min-height: 400px;
                width: 100%;
                display: flex;
                flex-direction: column;
            `}
        >
            <Snackbar
                open={!!state.notification.message}
                handleClose={callbacks.closeNotification}
                {...state.notification}
            />

            <div
                css={css`
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    padding-bottom: 12px;
                `}
            >
                <div
                    css={css`
                        display: flex;
                        align-items: center;
                        gap: 12px;
                    `}
                >
                    <TextField
                        variant="outlined"
                        size="small"
                        placeholder="Search by SKU..."
                        value={state.searchTerm}
                        onChange={(e) => callbacks.setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        css={css`
                            background-color: white;
                            min-width: 280px;
                        `}
                    />
                    {isCarrierAdmin ? (
                        <TextField
                            select
                            variant="outlined"
                            size="small"
                            label="Owned by"
                            value={selectedEntity}
                            onChange={(e) => callbacks.setSelectedEntity(e.target.value)}
                            css={css`
                                background-color: white;
                                min-width: 220px;
                            `}
                        >
                            <MenuItem value="">All Entities</MenuItem>
                            {orgClients.map((c) => (
                                <MenuItem key={c.client_id} value={c.client_id}>
                                    {c.business_name || c.client_id}
                                </MenuItem>
                            ))}
                        </TextField>
                    ) : null}
                </div>
                {readOnly ? null : (
                    <div
                        css={css`
                            display: flex;
                            gap: 12px;
                        `}
                    >
                        <PrimaryButton onClick={callbacks.openImport}>Import CSV</PrimaryButton>
                        <PrimaryButton onClick={callbacks.openAdd}>Add SKU</PrimaryButton>
                    </div>
                )}
            </div>

            <TableContainer
                css={css`
                    flex: 1 1 0;
                    min-height: 0;
                    overflow: auto;
                    border: 1px solid ${colors.greys.border};
                    border-radius: 4px;
                    background-color: white;
                `}
            >
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            {columns.map((col) => (
                                <TableCell
                                    key={col.id}
                                    align={col.align || 'left'}
                                    sortDirection={sortBy?.id === col.id ? (sortBy.desc ? 'desc' : 'asc') : false}
                                    css={css`
                                        font-weight: 700;
                                        background-color: white;
                                    `}
                                >
                                    {col.sortable ? (
                                        <TableSortLabel
                                            active={sortBy?.id === col.id}
                                            direction={sortBy?.id === col.id && sortBy.desc ? 'desc' : 'asc'}
                                            onClick={onSort(col.id)}
                                        >
                                            {col.label}
                                        </TableSortLabel>
                                    ) : (
                                        col.label
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {state.skus.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={columns.length}>
                                    <GreyBody>{emptyText}</GreyBody>
                                </TableCell>
                            </TableRow>
                        ) : (
                            state.skus.map((sku) => {
                                const manageable = canManage(sku);
                                return (
                                    <TableRow
                                        key={sku.item_dimension_id}
                                        hover={manageable}
                                        onClick={() => manageable && callbacks.openEdit(sku)}
                                        css={css`
                                            cursor: ${manageable ? 'pointer' : 'default'};
                                        `}
                                    >
                                        {isCarrierAdmin ? (
                                            <TableCell>{sku.client?.business_name || '--'}</TableCell>
                                        ) : null}
                                        <TableCell>{sku.sku}</TableCell>
                                        <TableCell>{sku.description || '--'}</TableCell>
                                        <TableCell>
                                            <AssemblyInstructions documents={sku.documents} />
                                        </TableCell>
                                        <TableCell>{formatServiceTime(sku)}</TableCell>
                                        <TableCell>{formatWeight(sku)}</TableCell>
                                        <TableCell>{formatDimensions(sku)}</TableCell>
                                        <TableCell>{formatInventoryRange(sku)}</TableCell>
                                        {readOnly ? null : (
                                            <TableCell align="right">
                                                {manageable ? (
                                                    <Tooltip title="Delete">
                                                        <IconButton
                                                            size="small"
                                                            aria-label={`Delete ${sku.sku}`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                callbacks.requestDelete(sku);
                                                            }}
                                                            css={css`
                                                                color: ${colors.reds.primary};
                                                            `}
                                                        >
                                                            <DeleteOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                ) : null}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Grid
                container
                justifyContent="center"
                css={css`
                    padding: 1rem 0;
                `}
            >
                <OnwardPagination
                    count={pageCount}
                    page={state.page + 1}
                    onChange={(_, p) => callbacks.setPage(p - 1)}
                />
            </Grid>

            <SKUModal
                open={!!state.editModal}
                sku={state.editModal?.item_dimension_id ? state.editModal : null}
                onClose={callbacks.closeEdit}
                onSave={callbacks.save}
                loading={loading.saving}
            />
            <DeleteSKUModal
                open={!!state.deleteRow}
                onCancel={callbacks.cancelDelete}
                onConfirm={callbacks.confirmDelete}
                loading={loading.deleting}
            />
            <SKUImportModal
                open={state.importOpen}
                onClose={callbacks.closeImport}
                onImport={callbacks.runImport}
                loading={loading.importing}
            />
        </div>
    );
};

const withContext = (Component) => (props) =>
    (
        <ContextProvider clientRef={props.clientRef} readOnly={props.readOnly}>
            <Component {...props} />
        </ContextProvider>
    );

export default withContext(SKUs);
